import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import {
  AuthProvider,
  ISSUE_TEXT_MAX_LENGTH,
  MediaKind,
  MESSAGE_MAX_LENGTH,
  MessageStatus,
  MessageType,
  PHOTO_ALLOWED_MIME_TYPES,
  PHOTO_MAX_SIZE_BYTES,
  REPORT_REASONS,
  TYPING_THROTTLE_MS,
  VOICE_MAX_DURATION_SEC,
  type MediaDto,
  type MessageDto,
  type ReportReason,
} from '@anon-chat/shared';
import { Button, Modal, Screen } from '@/components/ui';
import { PhotoViewer } from './PhotoViewer';
import { errorText, nickname, t, useTranslations } from '@/shared/i18n';
import { VoiceRecorder, decodeWaveform, isVoiceSupported } from '@/shared/voice-recorder';
import { api } from '@/shared/api';
import { collectDiagnostics } from '@/shared/diagnostics';
import { haptic } from '@/shared/telegram';
import { TelegramLoginButton } from '@/components/telegram-login';
import { WriteAccessNotice } from '@/components/write-access-notice';
import {
  forgetFriendIntent,
  readFriendIntent,
  rememberFriendIntent,
} from '@/shared/friend-intent';
import type { BotLogin } from '@/shared/use-bot-login';
import { useChatStore } from '@/store/chat.store';
import { useSessionStore } from '@/store/session.store';

/** Сколько нужно увести палец влево, чтобы запись отменилась. */
const CANCEL_SHIFT_PX = 90;

/** Запись доступна не везде: нужен защищённый адрес и разрешение браузера. */
const voiceSupported = isVoiceSupported();

/** Сколько держать палец на сообщении, чтобы открылось меню действий. */
const LONG_PRESS_MS = 450;
/** Сдвиг больше этого — прокрутка списка, а не удержание. */
const LONG_PRESS_TOLERANCE_PX = 10;
/** Сколько подсвечивать сообщение после перехода к нему из цитаты. */
const HIGHLIGHT_MS = 1600;
/** Зазор между пузырём и меню действий и отступ меню от краёв экрана. */
const MENU_GAP_PX = 6;
const MENU_MARGIN_PX = 8;
/**
 * Насколько близко к концу ленты человек считается «следящим за разговором».
 * Ровно ноль не годится: пружинистая прокрутка на телефоне почти никогда не
 * останавливается точно на границе.
 */
const BOTTOM_ZONE_PX = 80;
/**
 * Высота места под индикатор «печатает…». Оно занято всегда, даже когда
 * индикатора нет: иначе его появление раздвигало бы ленту и весь разговор
 * прыгал бы вверх.
 */
const TYPING_SLOT_PX = 34;
/**
 * Сколько ждать, пока плавная прокрутка успокоится. Всё это время её
 * собственные события не считаются действиями человека.
 */
const AUTO_SCROLL_SETTLE_MS = 450;
/**
 * Сколько реплик с каждой стороны считать состоявшимся разговором.
 *
 * Раньше подсказки о дружбе нет смысла: предлагать сохранить человека, с
 * которым обменялись парой слов, — это про навязчивость, а не про пользу.
 */
const FRIEND_HINT_AFTER = 6;

export function ChatScreen({
  botLogin,
  issuesEnabled,
}: {
  botLogin: BotLogin;
  /** Кнопку обращений владелец может убрать из панели. */
  issuesEnabled: boolean;
}): React.ReactNode {
  const dictionary = useTranslations();
  const { chat, messages, partnerTyping, partnerOnline, partnerAuthAway, connected, replyTo } =
    useChatStore();
  const { isFriendChat, incomingRequest, friendshipJustMade, friendOutcome } = useChatStore();
  const requestSent = useChatStore((state) => state.friendRequestSent);
  const clearFriendOutcome = useChatStore((state) => state.clearFriendOutcome);
  const { requestFriendship, respondToRequest, removeFriend, openSavedChats } = useChatStore();
  const loadPendingRequest = useChatStore((state) => state.loadPendingRequest);
  const { sendText, sendPhotoFile, sendVoice, markRead, setTyping, leaveChat, report } =
    useChatStore();
  const { startReply, cancelReply } = useChatStore();

  const [draft, setDraft] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /**
   * Открытое меню действий: само сообщение и место пузыря на экране — меню
   * встаёт вплотную к нему, а не отдельным окном посреди экрана.
   */
  const [actionsFor, setActionsFor] = useState<{ message: MessageDto; anchor: DOMRect } | null>(
    null,
  );
  /** Сообщение, к которому только что перешли по цитате. */
  const [highlighted, setHighlighted] = useState<string | null>(null);
  /** Открытая на весь экран фотография. */
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  /** Подтверждение удаления из друзей: действие необратимо. */
  const [removeOpen, setRemoveOpen] = useState(false);
  /** Чем закончилось нажатие 🤝: об этом нужно сказать словами. */
  const [friendNotice, setFriendNotice] = useState<'sent' | 'already' | 'failed' | null>(null);
  /** Гостю сначала объясняют, зачем входить, и только потом отправляют запрос. */
  const [loginPrompt, setLoginPrompt] = useState(false);
  /** Подсказку о дружбе закрыли: во второй раз не показываем. */
  const [hintHidden, setHintHidden] = useState(false);
  /** Запрос в пути: кнопка гаснет на это время, а не навсегда. */
  const [requesting, setRequesting] = useState(false);
  /** Форма обращения о неполадке. */
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueText, setIssueText] = useState('');
  const [issueState, setIssueState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  /** Идёт запись голосового: композитор заменяется полосой записи. */
  const [recordingMs, setRecordingMs] = useState<number | null>(null);
  const recordingSec = recordingMs === null ? null : Math.floor(recordingMs / 1000);
  /** Насколько палец увели влево: по этому пути запись отменяется. */
  const [cancelShift, setCancelShift] = useState(0);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const pressOriginRef = useRef(0);
  /* Нажатие держится в ссылке, а не в состоянии: между нажатием и ответом на
     запрос микрофона проходит время, и отпускание может случиться раньше. */
  const pressedRef = useRef(false);

  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingSentAt = useRef(0);

  /**
   * Человек читает старое или следит за новым.
   *
   * Лента доводится до конца только во втором случае. Раньше прокрутка была
   * безусловной, и любое событие — включая появление «печатает…» — вырывало
   * человека из середины переписки.
   */
  const atBottom = useRef(true);
  const seenCount = useRef(0);
  /**
   * Идёт наша собственная прокрутка.
   *
   * Плавная прокрутка порождает свои же события, и по ним лента посреди
   * анимации выглядит «не внизу»: содержимое уже выросло, а положение ещё
   * догоняет. Из-за этого следующее сообщение подряд оставалось бы за нижним
   * краем — читающий видел бы, как разговор замирает.
   */
  const autoScrolling = useRef(false);
  const settleTimer = useRef<number | null>(null);

  const onListScroll = (): void => {
    const node = listRef.current;
    if (!node) {
      return;
    }

    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;

    /*
      Собственную прокрутку не принимаем за действие человека — но только пока
      она идёт к низу. Уход вверх посреди неё делает только человек, и раньше
      такое движение пропадало: лента считала, что он по-прежнему внизу, и
      следующее сообщение выдёргивало его обратно.
    */
    if (autoScrolling.current && distance <= BOTTOM_ZONE_PX) {
      return;
    }

    atBottom.current = distance <= BOTTOM_ZONE_PX;
  };

  useLayoutEffect(() => {
    const node = listRef.current;
    if (!node) {
      return;
    }

    const first = seenCount.current === 0;
    const added = messages.length - seenCount.current;
    seenCount.current = messages.length;

    if (added <= 0) {
      return;
    }

    // Своё сообщение доводится до конца всегда: его только что отправили и
    // ждут увидеть. Чужое — лишь если человек и так внизу.
    const own = messages[messages.length - 1]?.isMine === true;
    if (!first && !own && !atBottom.current) {
      return;
    }

    autoScrolling.current = true;
    atBottom.current = true;
    node.scrollTo({ top: node.scrollHeight, behavior: first ? 'auto' : 'smooth' });

    // Срок продлевается каждым следующим сообщением: пока разговор идёт
    // потоком, прокрутка не должна считать себя чужой.
    if (settleTimer.current !== null) {
      window.clearTimeout(settleTimer.current);
    }
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = null;
      autoScrolling.current = false;
    }, AUTO_SCROLL_SETTLE_MS);
  }, [messages]);

  useEffect(
    () => () => {
      if (settleTimer.current !== null) {
        window.clearTimeout(settleTimer.current);
      }
    },
    [],
  );

  // Отметка о прочтении — на последнее входящее сообщение.
  useEffect(() => {
    const lastIncoming = [...messages].reverse().find((message) => !message.isMine);
    if (lastIncoming && document.visibilityState === 'visible') {
      markRead(lastIncoming.id);
    }
  }, [messages, markRead]);

  // Прозвище выводится из идентификатора участника: у каждого диалога он
  // свой, поэтому имя новое при каждой встрече и одинаковое у обеих сторон.
  const partnerName = chat ? nickname(chat.partner.anonymousId) : dictionary.chat.anonymous;

  /*
    Дружба держится на учётной записи: гостевая живёт во вкладке, и связи не на
    чем держаться. Но кнопка есть и у гостя — именно она и объясняет, зачем
    входить; спрятать её значит, что о самой возможности никто не узнает. В
    мини-приложении гостей не бывает.
  */
  const hasAccount = useSessionStore(
    (state) => state.user?.authProvider === AuthProvider.TELEGRAM,
  );

  /** Отправка предложения — нажатием или сама, после возвращения из Telegram. */
  const sendRequest = useCallback(() => {
    setRequesting(true);
    void requestFriendship().then((error) => {
      setRequesting(false);

      /*
        Отказ не должен гасить кнопку насовсем: причин у него несколько, и
        объяснять их надо словами, а не неподвижной кнопкой.
      */
      if (!error) {
        setFriendNotice('sent');
        return;
      }

      setFriendNotice(error === 'ALREADY_FRIENDS' ? 'already' : 'failed');
    });
  }, [requestFriendship]);

  /**
   * Уход за входом. Намерение записывается до ухода: вкладка на телефоне может
   * заснуть или перезагрузиться, и просить нажать то же самое второй раз значит
   * терять людей ровно на том шаге, ради которого всё затевалось.
   */
  const loginFirst = useCallback(
    (requestId?: string) => {
      if (chat) {
        rememberFriendIntent({ chatId: chat.id, ...(requestId ? { requestId } : {}) });
      }
      haptic.impact('light');
      botLogin.begin();
    },
    [botLogin, chat],
  );

  // Вернулся вошедшим — доводим начатое до конца.
  const chatId = chat?.id;
  useEffect(() => {
    if (!hasAccount || !connected || !chatId) {
      return;
    }

    const intent = readFriendIntent();
    if (!intent || intent.chatId !== chatId) {
      return;
    }

    forgetFriendIntent();
    setLoginPrompt(false);
    if (intent.requestId) {
      void respondToRequest(true, intent.requestId);
    } else {
      sendRequest();
    }
  }, [hasAccount, connected, chatId, respondToRequest, sendRequest]);

  /*
    Ответ на своё предложение. Согласие меняло только ленту, а отказ не менял
    ничего — и человек оставался наедине с окном «предложение отправлено»,
    которое к тому времени уже неправда.
  */
  useEffect(() => {
    if (friendOutcome) {
      setFriendNotice(null);
    }
  }, [friendOutcome]);

  /*
    Предложение могло прийти, пока вкладки не было: оно живёт пятнадцать минут
    и переживает и перезагрузку, и уход человека входить.
  */
  useEffect(() => {
    if (chatId && !isFriendChat) {
      void loadPendingRequest(chatId);
    }
  }, [chatId, isFriendChat, loadPendingRequest]);

  /*
    Подсказка о дружбе. Иконку в шапке легко не заметить, и человек уходит,
    не узнав, что собеседника можно было оставить. Появляется она не сразу и
    не насовсем: разговор должен состояться, а закрыть её — одно нажатие.
  */
  const showFriendHint = useMemo(() => {
    if (isFriendChat || hintHidden || requestSent || incomingRequest) {
      return false;
    }

    let mine = 0;
    let theirs = 0;
    for (const message of messages) {
      if (message.isMine) {
        mine += 1;
      } else {
        theirs += 1;
      }
    }
    return mine >= FRIEND_HINT_AFTER && theirs >= FRIEND_HINT_AFTER;
  }, [messages, isFriendChat, hintHidden, requestSent, incomingRequest]);

  /** Есть что отправить — показывается самолётик, иначе микрофон. */
  const showSend = Boolean(draft.trim()) || !voiceSupported;

  const statusText = partnerTyping
    ? dictionary.chat.typing
    : partnerOnline
      ? dictionary.chat.online
      : partnerAuthAway
        ? dictionary.chat.authAway
        : dictionary.chat.offline;

  const onDraftChange = (value: string) => {
    setDraft(value.slice(0, MESSAGE_MAX_LENGTH));

    // Событие «печатает» не чаще, чем раз в TYPING_THROTTLE_MS: иначе каждый
    // символ порождал бы отдельное сообщение по сокету.
    const now = Date.now();
    if (value && now - typingSentAt.current > TYPING_THROTTLE_MS) {
      typingSentAt.current = now;
      setTyping(true);
    }
    if (!value) {
      setTyping(false);
    }
  };

  const onSend = () => {
    const value = draft.trim();
    if (!value) {
      return;
    }
    setDraft('');
    setTyping(false);
    haptic.impact('light');
    void sendText(value);

    // Возврат курсора в поле: на телефоне уход фокуса закрывает клавиатуру, и
    // человеку приходится открывать её заново перед каждой репликой.
    inputRef.current?.focus();
  };

  const onPickPhoto = async (file: File | undefined) => {
    if (!file || !chat) {
      return;
    }

    if (file.size > PHOTO_MAX_SIZE_BYTES) {
      setUploadError(dictionary.chat.photoTooLarge);
      return;
    }
    if (!PHOTO_ALLOWED_MIME_TYPES.includes(file.type as (typeof PHOTO_ALLOWED_MIME_TYPES)[number])) {
      setUploadError(dictionary.chat.photoUnsupported);
      return;
    }

    setUploadError(null);
    setUploading(true);
    try {
      await sendPhotoFile(file);
    } catch (error) {
      setUploadError(errorText((error as { code?: string }).code));
    } finally {
      setUploading(false);
      if (fileRef.current) {
        fileRef.current.value = '';
      }
    }
  };

  // Счётчик записи и остановка по достижении предела: держать палец на кнопке
  // четверть часа никто не станет, а забыть про идущую запись — запросто.
  useEffect(() => {
    if (recordingMs === null) {
      return;
    }

    // Сотые доли: счётчик, меняющийся раз в секунду, выглядит замершим, а
    // человеку нужно видеть, что запись идёт.
    const startedAt = Date.now() - recordingMs;
    const timer = window.setInterval(() => {
      const passed = Date.now() - startedAt;
      setRecordingMs(passed);

      if (passed >= VOICE_MAX_DURATION_SEC * 1000) {
        void finishRecording();
      }
    }, 100);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingMs === null]);

  const startRecording = async () => {
    setVoiceError(null);
    const recorder = new VoiceRecorder();

    try {
      await recorder.start();
    } catch (error) {
      // Отказ в доступе выглядит так же, как отсутствие микрофона: человеку
      // важно понять, что дело в разрешении, а не в поломке.
      const denied = (error as { name?: string }).name === 'NotAllowedError';
      setVoiceError(denied ? dictionary.chat.voiceDenied : dictionary.chat.voiceFailed);
      return;
    }

    recorderRef.current = recorder;
    setRecordingMs(0);
    haptic.impact('medium');

    // Кнопку отпустили, пока браузер спрашивал разрешение: записывать нечего.
    if (!pressedRef.current) {
      cancelRecording();
      setVoiceError(dictionary.chat.voiceHoldHint);
    }
  };

  const finishRecording = async () => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    setRecordingMs(null);

    if (!recorder) {
      return;
    }

    const recording = await recorder.stop();
    if (!recording) {
      return;
    }

    haptic.impact('light');
    try {
      await sendVoice(recording.blob, recording.durationSec, recording.waveform);
    } catch (error) {
      setVoiceError(errorText((error as { code?: string }).code));
    }
  };

  const cancelRecording = () => {
    pressedRef.current = false;
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setRecordingMs(null);
    setCancelShift(0);
    haptic.notify('warning');
  };

  /*
    Запись идёт, пока кнопку держат: отпустили — отправляется, увели палец
    влево — отменяется. Так это устроено в мессенджерах, и рука уже знает жест.
  */
  const onMicPressStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pressedRef.current = true;
    pressOriginRef.current = event.clientX;
    setCancelShift(0);
    void startRecording();
  };

  const onMicMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!pressedRef.current || !recorderRef.current) {
      return;
    }

    const shift = Math.max(0, pressOriginRef.current - event.clientX);
    setCancelShift(shift);

    if (shift > CANCEL_SHIFT_PX) {
      pressedRef.current = false;
      cancelRecording();
    }
  };

  const onMicPressEnd = () => {
    pressedRef.current = false;
    if (!recorderRef.current) {
      return;
    }
    setCancelShift(0);
    void finishRecording();
  };

  const onReport = async (reason: ReportReason) => {
    const ok = await report(reason);
    setReportOpen(false);
    haptic.notify(ok ? 'success' : 'error');
  };

  /**
   * Меню действий не открывается для неподтверждённых сообщений: у них ещё нет
   * серверного идентификатора, и ответить на такое сообщение нельзя.
   */
  const onMessageHold = (message: MessageDto, anchor: DOMRect) => {
    if (message.status === MessageStatus.SENDING || message.status === MessageStatus.FAILED) {
      return;
    }
    haptic.impact('medium');
    setActionsFor({ message, anchor });
  };

  const onReply = (message: MessageDto) => {
    startReply(message);
    setActionsFor(null);
    inputRef.current?.focus();
  };

  /**
   * Переход к оригиналу по нажатию на цитату. Сообщение могло остаться за
   * пределами загруженной истории — тогда переходить некуда, и нажатие просто
   * ничего не делает.
   */
  const jumpToMessage = (messageId: string) => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
    if (!node) {
      return;
    }
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlighted(messageId);
    window.setTimeout(
      () => setHighlighted((current) => (current === messageId ? null : current)),
      HIGHLIGHT_MS,
    );
  };

  return (
    <Screen>
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        {/*
          Стрелка — только у сохранённого диалога: там она ведёт в «Мои чаты».
          Из случайного разговора уходят завершением, и второй кнопки с тем же
          действием в шапке быть не должно.
        */}
        {isFriendChat ? (
          <button
            type="button"
            aria-label={dictionary.common.back}
            className="flex size-9 items-center justify-center rounded-full text-text-muted active:bg-surface"
            onClick={() => void openSavedChats()}
          >
            <svg viewBox="0 0 24 24" className="size-6" aria-hidden>
              <path
                d="M15 5l-7 7 7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}

        <div className="min-w-0 flex-1">
          <p className="truncate text-[16px] font-medium text-text">{partnerName}</p>
          <p
            className={clsx(
              'truncate text-[13px]',
              partnerTyping ? 'text-accent' : partnerOnline ? 'text-success' : 'text-text-muted',
            )}
          >
            {statusText}
          </p>
        </div>

        {/*
          Завершение диалога — первым в ряду и не кнопкой внизу: там она отнимала
          у переписки полсотни пикселей на каждом экране. Уводит к параметрам
          поиска, где всё уже заполнено прошлым выбором, — новый собеседник в
          одно нажатие.

          Крестик обычного цвета, а не красного: красным в шапке помечена жалоба,
          и два тревожных знака рядом сбивают с толку. Завершить диалог —
          обыденное действие, а не крайняя мера.
        */}
        {isFriendChat ? null : (
          <button
            type="button"
            aria-label={dictionary.chat.leave}
            className="flex size-9 items-center justify-center rounded-full text-text active:bg-surface"
            onClick={() => {
              haptic.impact('medium');
              void leaveChat();
            }}
          >
            <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/*
          Дружба живёт в шапке, а не отдельной кнопкой внизу: там уже две, и
          третья съела бы высоту у самой переписки.
        */}
        {isFriendChat ? (
          <button
            type="button"
            aria-label={dictionary.chat.removeFriend}
            className="flex size-9 items-center justify-center rounded-full text-text-muted active:bg-surface"
            onClick={() => setRemoveOpen(true)}
          >
            <span className="text-[17px] leading-none">👋</span>
          </button>
        ) : (
          <button
            type="button"
            aria-label={dictionary.chat.befriend}
            disabled={requestSent || requesting}
            className="flex size-9 items-center justify-center rounded-full text-accent active:bg-surface disabled:opacity-40"
            onClick={() => {
              if (!hasAccount) {
                setLoginPrompt(true);
                return;
              }
              haptic.impact('light');
              sendRequest();
            }}
          >
            <span className="text-[17px] leading-none">🤝</span>
          </button>
        )}

        {/*
          Жалоба — про собеседника, обращение — про приложение. Рядом, но это
          разные вещи, и путать их нельзя: одно ведёт к блокировке человека,
          другое к починке кнопки. Жучок владелец может убрать из панели: когда
          поток поломок сходит на нет, кнопка только занимает место.
        */}
        {issuesEnabled ? (
          <button
            type="button"
            aria-label={dictionary.chat.reportIssue}
            className="flex size-9 items-center justify-center rounded-full active:bg-surface"
            onClick={() => {
              setIssueState('idle');
              setIssueOpen(true);
            }}
          >
            <span className="text-[16px] leading-none">🐞</span>
          </button>
        ) : null}

        <button
          type="button"
          aria-label={dictionary.report.action}
          className="flex size-9 items-center justify-center rounded-full text-danger active:bg-danger/15"
          onClick={() => {
            haptic.impact('medium');
            setReportOpen(true);
          }}
        >
          <span className="text-xl leading-none">⚠</span>
        </button>
      </header>

      <div
        ref={listRef}
        onScroll={onListScroll}
        className="no-scroll-anchor flex-1 overflow-y-auto overscroll-contain scrollbar-hidden"
      >
        {/*
          Переписка прижата к нижнему краю: в начале разговора пара реплик
          висела бы под самым заголовком, а взгляд ищет их там же, где потом
          появятся новые, — внизу, у поля ввода.
        */}
        <div className="flex min-h-full flex-col justify-end gap-1.5 px-3 py-4">
          <AnimatePresence initial={false}>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                partnerName={partnerName}
                highlighted={highlighted === message.id}
                onHold={(anchor) => onMessageHold(message, anchor)}
                onQuoteClick={jumpToMessage}
                onPhotoClick={setPhotoUrl}
              />
            ))}
          </AnimatePresence>

          {showFriendHint ? (
            <div className="flex items-center gap-1.5 self-center">
              <button
                type="button"
                className="rounded-lg bg-surface px-3 py-1.5 text-center text-[12px] leading-snug text-text-muted active:bg-border"
                onClick={() => {
                  if (!hasAccount) {
                    setLoginPrompt(true);
                    return;
                  }
                  haptic.impact('light');
                  sendRequest();
                }}
              >
                {hasAccount ? dictionary.chat.friendHint : dictionary.chat.friendHintGuest}
              </button>
              <button
                type="button"
                aria-label={dictionary.common.close}
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] text-text-muted active:bg-surface"
                onClick={() => setHintHidden(true)}
              >
                ✕
              </button>
            </div>
          ) : null}

          {friendshipJustMade ? (
            <>
              <p className="self-center rounded-lg bg-success/15 px-3 py-1 text-[12px] text-success">
                {dictionary.chat.friendsNow}
              </p>
              {/*
                Разрешение спрашивается здесь, а не при первом уведомлении:
                прока от напоминаний ещё нет, а смысл вопроса сейчас очевиден —
                собеседник только что сохранился.
              */}
              <WriteAccessNotice placement="inline" />
            </>
          ) : null}

          {/*
            Место под индикатор занято всегда. Появись он сам по себе — лента
            стала бы выше, а она прижата к нижнему краю: весь разговор дёрнулся
            бы вверх ровно в тот момент, когда собеседник начал печатать.
          */}
          <div className="shrink-0" style={{ height: TYPING_SLOT_PX }}>
            {partnerTyping ? <TypingIndicator label={dictionary.chat.typing} /> : null}
          </div>
        </div>
      </div>

      {/*
        Обрыв собственной связи. Без этой строки отправленные сообщения просто
        краснеют, и непонятно, дело в собеседнике или в сети.
      */}
      {!connected ? (
        <p className="border-t border-border bg-warning/10 px-4 py-1.5 text-center text-[13px] text-warning">
          {dictionary.chat.connectionLost}
        </p>
      ) : null}

      {uploadError ? (
        <p className="px-4 pb-1 text-center text-[13px] text-danger">{uploadError}</p>
      ) : null}

      {/*
        Что именно цитируется, видно до отправки: иначе после нескольких реплик
        подряд непонятно, к какой из них относится ответ.
      */}
      {replyTo ? (
        <div className="flex items-center gap-2 border-t border-border bg-surface/60 px-3 py-2">
          <span className="w-0.5 shrink-0 self-stretch rounded-full bg-accent" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-medium text-accent">
              {replyTo.isMine ? dictionary.chat.you : partnerName}
            </p>
            <p className="truncate text-[13px] text-text-muted">{quoteText(replyTo, dictionary)}</p>
          </div>
          <button
            type="button"
            aria-label={dictionary.chat.cancelReply}
            onClick={cancelReply}
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-muted active:bg-surface-raised"
          >
            <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ) : null}

      {voiceError ? (
        <p className="px-4 pb-1 text-center text-[13px] text-danger">{voiceError}</p>
      ) : null}

      {/*
        Во время записи композитор заменяется полосой: писать и записывать
        одновременно нельзя, а видеть время и кнопку отмены — обязательно.
      */}
      {/*
        Кнопка микрофона остаётся на месте во время записи: она — якорь жеста,
        и если её убрать, палец отпускают уже над пустотой, а событие до неё не
        доходит. Меняется только левая часть композитора.
      */}
      <div className="flex items-end gap-2 border-t border-border px-3 py-2">
        {recordingSec === null ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={PHOTO_ALLOWED_MIME_TYPES.join(',')}
              className="hidden"
              onChange={(event) => void onPickPhoto(event.target.files?.[0])}
            />
            <button
              type="button"
              aria-label={dictionary.chat.sendPhoto}
              disabled={uploading}
              className="flex size-10 shrink-0 items-center justify-center rounded-full text-text-muted active:bg-surface disabled:opacity-40"
              onClick={() => fileRef.current?.click()}
            >
              <svg viewBox="0 0 24 24" className="size-6" aria-hidden>
                <path
                  d="M21 15l-5-5L5 21M3 5h18v14H3z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  onSend();
                }
              }}
              rows={1}
              placeholder={dictionary.chat.inputPlaceholder}
              className="max-h-28 min-h-[40px] flex-1 resize-none rounded-2xl bg-surface px-4 py-2.5 text-[16px] text-text outline-none placeholder:text-text-muted"
            />
          </>
        ) : (
          <div className="flex h-10 flex-1 items-center gap-3">
            {/* Красная точка мигает: без неё полоса выглядит статичной подписью. */}
            <motion.span
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{ duration: 1.1, repeat: Infinity }}
              className="size-2.5 shrink-0 rounded-full bg-danger"
            />
            <span className="w-[72px] shrink-0 tabular-nums text-[15px] text-text">
              {formatRecordingClock(recordingMs ?? 0)}
            </span>

            {/*
              Подсказка уезжает вслед за пальцем и гаснет по мере приближения к
              отмене: движение само объясняет, что произойдёт.
            */}
            <motion.span
              animate={{ x: -Math.min(cancelShift, CANCEL_SHIFT_PX) }}
              transition={{ type: 'tween', duration: 0.05 }}
              style={{ opacity: 1 - Math.min(cancelShift / CANCEL_SHIFT_PX, 0.85) }}
              className="flex flex-1 items-center justify-center gap-1 text-[14px] text-text-muted"
            >
              <span aria-hidden>‹</span>
              {dictionary.chat.voiceSlideToCancel}
            </motion.span>
          </div>
        )}

        {/*
          Обе кнопки остаются в разметке, меняется только видимость. Подмена
          одной на другую перестраивает композитор вплотную к полю ввода, и на
          телефоне это гасит клавиатуру посреди набора — на каждом первом
          символе и после каждой отправки.
        */}
        <>
          <button
            type="button"
            aria-label="Отправить"
            disabled={!draft.trim()}
            hidden={!showSend}
            /*
              Нажатие не уводит фокус из поля ввода: иначе телефон закрывает
              клавиатуру ещё до отправки, и вернуть её программно уже нельзя —
              браузеры разрешают это только внутри жеста пользователя.
            */
            onPointerDown={(event) => event.preventDefault()}
            onClick={onSend}
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-white transition-opacity disabled:opacity-30"
          >
            <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
              <path d="M3 20l18-8L3 4l4 8-4 8z" fill="currentColor" />
            </svg>
          </button>

          <motion.button
            type="button"
            aria-label={dictionary.chat.recordVoice}
            hidden={showSend}
            onPointerDown={onMicPressStart}
            onPointerMove={onMicMove}
            onPointerUp={onMicPressEnd}
            onPointerCancel={cancelRecording}
            onContextMenu={(event) => event.preventDefault()}
            animate={{ scale: recordingSec === null ? 1 : 1.35 }}
            transition={{ type: 'spring', stiffness: 400, damping: 22 }}
            className="flex size-10 shrink-0 touch-none select-none items-center justify-center
                       rounded-full bg-accent text-white"
          >
            <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
              <path
                d="M12 3a3 3 0 013 3v6a3 3 0 01-6 0V6a3 3 0 013-3zM5 11a7 7 0 0014 0M12 18v3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.button>
        </>
      </div>

      <AnimatePresence>
        {actionsFor ? (
          <MessageActions
            anchor={actionsFor.anchor}
            bounds={listRef.current?.getBoundingClientRect() ?? null}
            isMine={actionsFor.message.isMine}
            label={dictionary.chat.reply}
            onReply={() => onReply(actionsFor.message)}
            onClose={() => setActionsFor(null)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {photoUrl ? <PhotoViewer url={photoUrl} onClose={() => setPhotoUrl(null)} /> : null}
      </AnimatePresence>

      <AnimatePresence>
        {issueOpen ? (
          <Modal
            open
            title={issueState === 'sent' ? dictionary.chat.issueSentTitle : dictionary.chat.issueTitle}
            onClose={() => setIssueOpen(false)}
          >
            {issueState === 'sent' ? (
              <>
                <p className="mb-4 text-center text-[14px] leading-relaxed text-text-muted">
                  {dictionary.chat.issueSentText}
                </p>
                <Button onClick={() => setIssueOpen(false)}>{dictionary.common.close}</Button>
              </>
            ) : (
              <>
                <p className="mb-3 text-[13px] leading-relaxed text-text-muted">
                  {dictionary.chat.issueText}
                </p>
                <textarea
                  value={issueText}
                  onChange={(event) => setIssueText(event.target.value.slice(0, ISSUE_TEXT_MAX_LENGTH))}
                  rows={4}
                  placeholder={dictionary.chat.issuePlaceholder}
                  className="mb-3 w-full resize-none rounded-xl bg-surface-raised px-3 py-2 text-[15px] text-text outline-none placeholder:text-text-muted"
                />
                {issueState === 'failed' ? (
                  <p className="mb-2 text-center text-[13px] text-danger">
                    {dictionary.chat.issueFailed}
                  </p>
                ) : null}
                <Button
                  disabled={issueText.trim().length < 5 || issueState === 'sending'}
                  onClick={() => {
                    setIssueState('sending');
                    void api
                      .reportIssue(issueText.trim(), chat?.id ?? null, collectDiagnostics('chat'))
                      .then(() => {
                        setIssueText('');
                        setIssueState('sent');
                      })
                      .catch(() => setIssueState('failed'));
                  }}
                >
                  {dictionary.chat.issueSend}
                </Button>
              </>
            )}
          </Modal>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {friendNotice ? (
          <Modal
            open
            title={
              friendNotice === 'already'
                ? dictionary.chat.friendAlreadyTitle
                : friendNotice === 'sent'
                  ? dictionary.chat.friendSentTitle
                  : dictionary.chat.friendFailedTitle
            }
            onClose={() => setFriendNotice(null)}
          >
            <p className="mb-4 text-center text-[14px] leading-relaxed text-text-muted">
              {friendNotice === 'already'
                ? dictionary.chat.friendAlreadyText
                : friendNotice === 'sent'
                  ? dictionary.chat.friendSentText
                  : dictionary.chat.friendFailedText}
            </p>
            <Button onClick={() => setFriendNotice(null)}>{dictionary.common.close}</Button>
          </Modal>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {friendOutcome ? (
          <Modal
            open
            title={
              friendOutcome === 'accepted'
                ? dictionary.chat.friendsNowTitle
                : friendOutcome === 'already'
                  ? dictionary.chat.friendAlreadyTitle
                  : dictionary.chat.friendDeclinedTitle
            }
            onClose={clearFriendOutcome}
          >
            <p className="mb-4 text-center text-[14px] leading-relaxed text-text-muted">
              {friendOutcome === 'accepted'
                ? dictionary.chat.friendsNowText
                : friendOutcome === 'already'
                  ? dictionary.chat.friendAlreadyText
                  : dictionary.chat.friendDeclinedText}
            </p>
            <Button onClick={clearFriendOutcome}>{dictionary.common.close}</Button>
          </Modal>
        ) : null}
      </AnimatePresence>

      {/*
        Гость нажал 🤝. Здесь и объясняется, зачем вход: не запретом, а
        предложением — вернуться к разговору позже можно только так.
      */}
      <AnimatePresence>
        {loginPrompt ? (
          <Modal
            open
            title={dictionary.chat.friendGuestTitle}
            onClose={() => {
              botLogin.cancel();
              forgetFriendIntent();
              setLoginPrompt(false);
            }}
          >
            <p className="mb-4 text-center text-[14px] leading-relaxed text-text-muted">
              {dictionary.chat.friendGuestText}
            </p>
            <div className="flex flex-col gap-3">
              <TelegramLoginButton botLogin={{ ...botLogin, begin: () => loginFirst() }} />
              <button
                type="button"
                className="text-[13px] text-text-muted underline-offset-2 hover:underline"
                onClick={() => {
                  botLogin.cancel();
                  forgetFriendIntent();
                  setLoginPrompt(false);
                }}
              >
                {dictionary.chat.friendLater}
              </button>
            </div>
          </Modal>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {incomingRequest ? (
          <Modal
            open
            title={t(dictionary.chat.friendRequestTitle, {
              name: nickname(incomingRequest.fromAnonymousId),
            })}
            onClose={() => void respondToRequest(false)}
          >
            <p className="mb-4 text-center text-[14px] leading-relaxed text-text-muted">
              {hasAccount
                ? dictionary.chat.friendRequestText
                : dictionary.chat.friendRequestGuestText}
            </p>
            {hasAccount ? (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => void respondToRequest(false)}>
                  {dictionary.chat.friendDecline}
                </Button>
                <Button
                  onClick={() => {
                    haptic.notify('success');
                    void respondToRequest(true);
                  }}
                >
                  {dictionary.chat.friendAccept}
                </Button>
              </div>
            ) : (
              /*
                Гостю вход и согласие — одно действие, а не два: предложение
                исполнится само, как только он вернётся из Telegram.
              */
              <div className="flex flex-col gap-3">
                <TelegramLoginButton
                  botLogin={{ ...botLogin, begin: () => loginFirst(incomingRequest.id) }}
                  label={dictionary.chat.friendAcceptWithLogin}
                />
                <button
                  type="button"
                  className="text-[13px] text-text-muted underline-offset-2 hover:underline"
                  onClick={() => void respondToRequest(false)}
                >
                  {dictionary.chat.friendDecline}
                </button>
              </div>
            )}
          </Modal>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {removeOpen && chat ? (
          <Modal open title={dictionary.chat.removeFriendTitle} onClose={() => setRemoveOpen(false)}>
            <p className="mb-4 text-center text-[14px] leading-relaxed text-text-muted">
              {dictionary.chat.removeFriendText}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setRemoveOpen(false)}>
                {dictionary.common.cancel}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setRemoveOpen(false);
                  void removeFriend(chat.id);
                }}
              >
                {dictionary.chat.removeFriend}
              </Button>
            </div>
          </Modal>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {reportOpen ? (
          <Modal open title={dictionary.report.confirmTitle} onClose={() => setReportOpen(false)}>
            <p className="mb-4 text-center text-[14px] text-text-muted">
              {dictionary.report.confirmText}
            </p>
            <div className="mb-3 flex flex-col gap-2">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  className="rounded-xl bg-surface-raised px-4 py-3 text-left text-[15px] text-text active:bg-border"
                  onClick={() => void onReport(reason)}
                >
                  {REASON_LABELS(dictionary)[reason]}
                </button>
              ))}
            </div>
            <Button variant="ghost" onClick={() => setReportOpen(false)}>
              {dictionary.common.cancel}
            </Button>
          </Modal>
        ) : null}
      </AnimatePresence>
    </Screen>
  );
}

const REASON_LABELS = (
  dictionary: ReturnType<typeof useTranslations>,
): Record<ReportReason, string> => ({
  SPAM: dictionary.report.reasonSpam,
  INSULT: dictionary.report.reasonInsult,
  PORN: dictionary.report.reasonPorn,
  SCAM: dictionary.report.reasonScam,
  UNDERAGE: dictionary.report.reasonUnderage,
  OTHER: dictionary.report.reasonOther,
});

/** Строка предпросмотра цитаты: у фотографии текста нет. */
function quoteText(
  quote: { type: MessageType; text: string | null },
  dictionary: ReturnType<typeof useTranslations>,
): string {
  if (quote.type === MessageType.PHOTO) {
    return dictionary.chat.replyPhoto;
  }
  if (quote.type === MessageType.VOICE) {
    return dictionary.chat.voiceMessage;
  }
  return quote.text ?? '';
}

/**
 * Меню действий над сообщением.
 *
 * Всплывает вплотную к пузырю: нижний лист посреди экрана ради одного пункта
 * уводит взгляд от того самого сообщения, о котором идёт речь.
 */
function MessageActions({
  anchor,
  bounds,
  isMine,
  label,
  onReply,
  onClose,
}: {
  anchor: DOMRect;
  /** Область переписки: меню не должно наезжать на поле ввода и заголовок. */
  bounds: DOMRect | null;
  isMine: boolean;
  label: string;
  onReply: () => void;
  onClose: () => void;
}): React.ReactNode {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Место считается после отрисовки: до неё неизвестен размер меню, а от него
  // зависит, встанет оно под сообщением или над ним.
  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!node) {
      return;
    }

    const { width, height } = node.getBoundingClientRect();
    const area = bounds ?? new DOMRect(0, 0, window.innerWidth, window.innerHeight);

    // Под последним сообщением места нет — там поле ввода, поэтому меню
    // переворачивается наверх, как в мессенджерах.
    const below = anchor.bottom + MENU_GAP_PX;
    const top =
      below + height > area.bottom - MENU_MARGIN_PX
        ? Math.max(area.top + MENU_MARGIN_PX, anchor.top - height - MENU_GAP_PX)
        : below;

    // Меню держится того края, к которому прижат пузырь.
    const preferred = isMine ? anchor.right - width : anchor.left;
    const left = Math.min(
      Math.max(area.left + MENU_MARGIN_PX, preferred),
      area.right - width - MENU_MARGIN_PX,
    );

    setPosition({ top, left });
  }, [anchor, bounds, isMine]);

  // Прокрутка увела бы сообщение, а меню осталось бы висеть на прежнем месте.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('scroll', onClose, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.14 }}
      // Подложка прозрачная: нажатие мимо закрывает меню, а разговор остаётся
      // видимым — затемнять его ради одного пункта незачем.
      className="fixed inset-0 z-40"
      onClick={onClose}
    >
      <div
        ref={menuRef}
        role="menu"
        style={{
          top: position?.top ?? anchor.bottom + MENU_GAP_PX,
          left: position?.left ?? anchor.left,
          // До подсчёта места меню невидимо: иначе оно мигнёт в левом углу.
          opacity: position ? 1 : 0,
        }}
        className="fixed z-50 min-w-[168px] overflow-hidden rounded-2xl bg-surface-raised py-1 shadow-[0_12px_32px_rgba(0,0,0,0.45)] ring-1 ring-border"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onReply}
          className="flex w-full items-center gap-3 px-4 py-3 text-left text-[16px] text-text active:bg-border"
        >
          <svg viewBox="0 0 24 24" className="size-5 text-text-muted" aria-hidden>
            <path
              d="M9 14L4 9l5-5M4 9h8a6 6 0 016 6v3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {label}
        </button>
      </div>
    </motion.div>
  );
}

/**
 * Вызов меню действий — каждым устройством по-своему.
 *
 * Пальцем — долгое нажатие: другого способа на сенсорном экране нет. Мышью —
 * правая кнопка, как во всех настольных программах; удержание левой для этого
 * не годится, им выделяют текст.
 *
 * Прокрутка списка не должна открывать меню, поэтому отсчёт прерывается при
 * сдвиге пальца — событий прокрутки касание внутри списка не порождает, зато
 * порождает pointermove.
 *
 * Наружу отдаётся место пузыря на экране: меню встаёт рядом с ним, а к моменту
 * срабатывания событие уже недоступно.
 */
function useLongPress(onLongPress: (anchor: DOMRect) => void): React.HTMLAttributes<HTMLElement> {
  const timer = useRef<number | null>(null);
  const origin = useRef({ x: 0, y: 0 });
  const pointerType = useRef<string>('');
  /** Меню уже открылось удержанием: щелчок по отпусканию гасится. */
  const opened = useRef(false);

  const cancel = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => cancel, []);

  return {
    onPointerDown: (event) => {
      cancel();
      opened.current = false;
      pointerType.current = event.pointerType;
      if (event.pointerType === 'mouse') {
        return;
      }

      origin.current = { x: event.clientX, y: event.clientY };
      const element = event.currentTarget;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        opened.current = true;
        onLongPress(element.getBoundingClientRect());
      }, LONG_PRESS_MS);
    },
    /*
      Палец отпускают уже с открытым меню, и браузер после этого шлёт обычный
      щелчок. Без перехвата удержание на фотографии открывало бы разом и меню, и
      полноэкранный просмотр.
    */
    onClickCapture: (event) => {
      if (opened.current) {
        opened.current = false;
        event.preventDefault();
        event.stopPropagation();
      }
    },
    onPointerMove: (event) => {
      const moved =
        Math.abs(event.clientX - origin.current.x) > LONG_PRESS_TOLERANCE_PX ||
        Math.abs(event.clientY - origin.current.y) > LONG_PRESS_TOLERANCE_PX;
      if (moved) {
        cancel();
      }
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu: (event) => {
      // Системное меню перекрыло бы своё: браузер показывает его и по правой
      // кнопке, и по долгому нажатию пальцем.
      event.preventDefault();

      // Пальцем меню уже открыто отсчётом — второй раз не нужно.
      if (pointerType.current === 'mouse') {
        onLongPress(event.currentTarget.getBoundingClientRect());
      }
    },
  };
}

function MessageBubble({
  message,
  partnerName,
  highlighted,
  onHold,
  onQuoteClick,
  onPhotoClick,
}: {
  message: MessageDto;
  partnerName: string;
  highlighted: boolean;
  onHold: (anchor: DOMRect) => void;
  onQuoteClick: (messageId: string) => void;
  onPhotoClick: (url: string) => void;
}): React.ReactNode {
  const dictionary = useTranslations();
  const longPress = useLongPress(onHold);
  const quote = message.replyTo;

  const time = useMemo(
    () =>
      new Date(message.createdAt).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [message.createdAt],
  );

  /*
    Без `layout`: сообщения только добавляются в конец и не меняются местами, а
    с ним framer-motion пересчитывал и анимировал положение всех пузырей на
    каждое новое — лента при этом подрагивала под тем, кто читает старое.
  */
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.18 }}
      data-message-id={message.id}
      className={clsx('flex', message.isMine ? 'justify-end' : 'justify-start')}
    >
      <div
        {...longPress}
        className={clsx(
          'max-w-[78%] rounded-bubble px-3 py-2 transition-shadow duration-300',
          message.isMine ? 'bg-bubble-own' : 'bg-bubble-other',
          message.status === MessageStatus.FAILED && 'opacity-60 ring-1 ring-danger',
          highlighted && 'ring-2 ring-accent',
        )}
      >
        {quote ? (
          <button
            type="button"
            aria-label={dictionary.chat.goToOriginal}
            onClick={() => onQuoteClick(quote.id)}
            className="mb-1.5 flex w-full items-stretch gap-2 rounded-lg bg-black/20 px-2 py-1 text-left active:opacity-70"
          >
            <span className="w-0.5 shrink-0 rounded-full bg-accent" />
            <span className="min-w-0">
              <span className="block text-[12px] font-medium text-accent">
                {quote.isMine ? dictionary.chat.you : partnerName}
              </span>
              {/*
                Приглушённый серый теряется на цветном пузыре — здесь тот же
                цвет текста, что и у самого сообщения, но полупрозрачный.
              */}
              <span className="block truncate text-[13px] text-text/80">
                {quoteText(quote, dictionary)}
              </span>
            </span>
          </button>
        ) : null}

        {message.media && message.media.kind === MediaKind.VOICE ? (
          <VoicePlayer media={message.media} sending={message.status === MessageStatus.SENDING} />
        ) : null}

        {message.media && message.media.kind === MediaKind.PHOTO ? (
          /*
            Пока фотография уходит, показывается её локальное превью. Оно
            приглушено и накрыто индикатором: иначе снимок выглядит
            отправленным, хотя загрузка ещё идёт.
          */
          <button
            type="button"
            aria-label={dictionary.chat.openPhoto}
            // Пока снимок уходит, открывать нечего: по ссылке ещё нет файла.
            disabled={message.status === MessageStatus.SENDING}
            onClick={() => onPhotoClick(message.media?.url ?? '')}
            className="relative mb-1 block w-full"
          >
            <img
              src={message.media.url}
              alt=""
              loading="lazy"
              className={clsx(
                'max-h-72 w-full rounded-xl object-cover transition-opacity duration-200',
                message.status === MessageStatus.SENDING && 'opacity-55',
              )}
            />
            {message.status === MessageStatus.SENDING ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Spinner />
              </span>
            ) : null}
          </button>
        ) : null}

        {message.text ? (
          <p className="whitespace-pre-wrap break-words text-[15px] leading-snug text-text">
            {message.text}
          </p>
        ) : null}

        <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-text-muted">
          <span>{time}</span>
          {message.isMine ? <StatusTicks status={message.status} /> : null}
        </div>
      </div>
    </motion.div>
  );
}


/**
 * Проигрыватель голосового.
 *
 * Родной элемент браузера выглядит чужеродно и не показывает длительность:
 * в записи, сделанной браузером, её часто нет вовсе. Поэтому кнопка, столбики
 * громкости и время рисуются свои, а длительность берётся из наших данных.
 */
function VoicePlayer({ media, sending }: { media: MediaDto; sending: boolean }): React.ReactNode {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playedSec, setPlayedSec] = useState(0);

  const bars = useMemo(() => decodeWaveform(media.waveform), [media.waveform]);
  const total = media.durationSec ?? 0;
  const progress = total > 0 ? Math.min(playedSec / total, 1) : 0;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      // Играет всегда одно: два голоса разом неразборчивы, а остановить второе
      // человек успевает не сразу.
      stopOtherVoices(audio);
      void audio.play();
      return;
    }
    audio.pause();
  };

  return (
    <div className={clsx('mb-1 flex items-center gap-3', sending && 'opacity-60')}>
      <audio
        ref={audioRef}
        src={media.url}
        preload="none"
        onPlay={(event) => {
          stopOtherVoices(event.currentTarget);
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPlayedSec(0);
        }}
        onTimeUpdate={(event) => setPlayedSec(event.currentTarget.currentTime)}
      />

      <button
        type="button"
        aria-label={playing ? 'Пауза' : 'Слушать'}
        onClick={toggle}
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-bubble-own"
      >
        {playing ? (
          <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
            <path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
            <path d="M8 5l11 7-11 7z" fill="currentColor" />
          </svg>
        )}
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/*
          Столбики отражают громкость записи: по ним видно, где речь, а где
          пауза, и докуда доиграно.
        */}
        <div className="flex h-6 items-center gap-[2px]">
          {(bars.length > 0 ? bars : FLAT_WAVEFORM).map((value, index, all) => (
            <span
              key={index}
              className={clsx(
                'w-[2px] shrink-0 rounded-full',
                index / all.length <= progress ? 'bg-white' : 'bg-white/35',
              )}
              style={{ height: `${Math.max(10, value * 100)}%` }}
            />
          ))}
        </div>

        <span className="text-[12px] tabular-nums text-text-muted">
          {formatClock(playing || playedSec > 0 ? playedSec : total)}
        </span>
      </div>
    </div>
  );
}

/**
 * Играющее сейчас голосовое.
 *
 * Хранится вне React: элементы разбросаны по пузырям, и общее состояние им
 * нужно ровно одно — кто звучит. Останавливать соседей через хранилище значило
 * бы гонять перерисовку всей переписки на каждое нажатие.
 */
let playingAudio: HTMLAudioElement | null = null;

function stopOtherVoices(next: HTMLAudioElement): void {
  if (playingAudio && playingAudio !== next) {
    playingAudio.pause();
  }
  playingAudio = next;
}

/** Время записи с сотыми: 0:02,70 — так его показывают в мессенджерах. */
function formatRecordingClock(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  const hundredths = Math.floor((ms % 1000) / 10);
  return `${minutes}:${String(seconds).padStart(2, '0')},${String(hundredths).padStart(2, '0')}`;
}

/** Длительность записи в привычном для звука виде: 0:03, 12:45. */
function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** Ровная полоска, когда столбиков нет: записи до этой версии их не хранят. */
const FLAT_WAVEFORM = Array.from({ length: 32 }, () => 0.35);

/** Индикатор отправки поверх превью: кольцо с вырезом, крутится. */
function Spinner(): React.ReactNode {
  return (
    <motion.span
      animate={{ rotate: 360 }}
      transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
      className="block size-7 rounded-full border-2 border-white/35 border-t-white"
    />
  );
}

function StatusTicks({ status }: { status: MessageStatus }): React.ReactNode {
  if (status === MessageStatus.SENDING) {
    return <span aria-label="Отправляется">⏳</span>;
  }
  if (status === MessageStatus.FAILED) {
    return <span className="text-danger">!</span>;
  }
  return (
    <span className={status === MessageStatus.READ ? 'text-accent' : undefined}>
      {status === MessageStatus.SENT ? '✓' : '✓✓'}
    </span>
  );
}

function TypingIndicator({ label }: { label: string }): React.ReactNode {
  return (
    <div className="flex h-full justify-start">
      <div
        className="flex items-center gap-1 rounded-bubble bg-bubble-other px-3"
        style={{ height: TYPING_SLOT_PX }}
      >
        <span className="sr-only">{label}</span>
        {[0, 0.15, 0.3].map((delay) => (
          <motion.span
            key={delay}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.1, repeat: Infinity, delay }}
            className="size-1.5 rounded-full bg-text-muted"
          />
        ))}
      </div>
    </div>
  );
}
